// Kids mode decoration: a smiling sun, a rainbow between two clouds, a little cloud and a few stars in the corners
// of the page. Pure decoration built from simple SVG shapes (no text): hidden from assistive tech, ignores the pointer
// and sits behind every page element (-z-10), so it never covers content. Server and browser render the same HTML;
// .kids-only shows it under html[data-mode='kids'] only. Motion lives in globals.css (Kids theme) and stops under
// prefers-reduced-motion.

import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface ShapeProps {
    className?: string
}

const RAYS = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330]

/** A round, smiling sun with rosy cheeks. */
export const KidsSun: FC<ShapeProps> = ({ className }): ReactNode => (
    <svg viewBox='0 0 120 120' fill='none' aria-hidden='true' focusable='false' className={className}>
        <g className='kid-sun-rays'>
            {RAYS.map(angle => (
                <line
                    key={angle}
                    x1='60'
                    y1='9'
                    x2='60'
                    y2='21'
                    transform={`rotate(${angle} 60 60)`}
                    strokeWidth='6'
                    strokeLinecap='round'
                    className='stroke-kid-orange'
                />
            ))}
        </g>
        <circle cx='60' cy='60' r='31' strokeWidth='4' className='fill-kid-sun stroke-kid-orange' />
        <circle cx='44' cy='67' r='5.5' opacity='0.55' className='fill-kid-berry' />
        <circle cx='76' cy='67' r='5.5' opacity='0.55' className='fill-kid-berry' />
        <ellipse cx='49' cy='54' rx='3.8' ry='4.8' className='fill-kid-on' />
        <ellipse cx='71' cy='54' rx='3.8' ry='4.8' className='fill-kid-on' />
        <circle cx='50.4' cy='52.2' r='1.3' className='fill-white' />
        <circle cx='72.4' cy='52.2' r='1.3' className='fill-white' />
        <path d='M47 68 Q60 80 73 68' strokeWidth='4' strokeLinecap='round' className='stroke-kid-on' />
    </svg>
)

const CLOUD_PATH = 'M10 30 A10 10 0 0 1 9 12 A14 14 0 0 1 34 7 A11 11 0 0 1 50 18 A6 6 0 0 1 48 30 Z'

/** A puffy cloud. */
export const KidsCloud: FC<ShapeProps> = ({ className }): ReactNode => (
    <svg viewBox='0 -2 58 35' fill='none' aria-hidden='true' focusable='false' className={className}>
        <path d={CLOUD_PATH} strokeWidth='3' strokeLinejoin='round' className='fill-white stroke-kid-sky' />
    </svg>
)

const BANDS = ['stroke-kid-berry', 'stroke-kid-orange', 'stroke-kid-sun', 'stroke-kid-grass', 'stroke-kid-sky']

/** A rainbow resting on two clouds. */
export const KidsRainbow: FC<ShapeProps> = ({ className }): ReactNode => (
    <svg viewBox='0 0 160 104' fill='none' aria-hidden='true' focusable='false' className={className}>
        {BANDS.map((band, i) => {
            const r = 66 - i * 9

            return (
                <path
                    key={band}
                    d={`M${80 - r} 90 A${r} ${r} 0 0 1 ${80 + r} 90`}
                    strokeWidth='9'
                    strokeLinecap='round'
                    className={band}
                />
            )
        })}
        <path d={CLOUD_PATH} transform='translate(-2 66)' strokeWidth='3' strokeLinejoin='round' className='fill-white stroke-kid-sky' />
        <path d={CLOUD_PATH} transform='translate(104 68) scale(0.9)' strokeWidth='3' strokeLinejoin='round' className='fill-white stroke-kid-sky' />
    </svg>
)

const STAR_TONE = {
    sun: 'fill-kid-sun stroke-kid-orange',
    sky: 'fill-kid-sky stroke-kid-sky-ink',
    berry: 'fill-kid-berry stroke-kid-berry-ink',
    grass: 'fill-kid-grass stroke-kid-grass-ink'
} as const

/** A chubby five-pointed star. */
export const KidsStar: FC<ShapeProps & { tone?: keyof typeof STAR_TONE }> = ({ className, tone = 'sun' }): ReactNode => (
    <svg viewBox='0 0 24 24' aria-hidden='true' focusable='false' className={className}>
        <path
            d='M12 2.6l2.75 5.6 6.15.9-4.45 4.3 1.05 6.1L12 16.6l-5.5 2.9 1.05-6.1L3.1 9.1l6.15-.9z'
            strokeWidth='1.6'
            strokeLinejoin='round'
            className={STAR_TONE[tone]}
        />
    </svg>
)

/** Mounted once by the page layout. */
export const KidsDecor: FC<{ className?: string }> = ({ className }): ReactNode => (
    <div aria-hidden='true' className={cn('kids-only kid-decor pointer-events-none fixed inset-0 -z-10 overflow-hidden', className)}>
        <KidsSun className='kid-decor-sun absolute -right-10 top-24 size-28 sm:-right-8 sm:size-36 xl:right-2 xl:size-44' />
        <KidsCloud className='kid-decor-cloud absolute left-[10%] top-[5.25rem] hidden w-24 sm:block lg:w-28' />
        <KidsRainbow className='kid-decor-rainbow absolute -left-6 bottom-24 w-40 sm:w-52 lg:bottom-4 lg:left-2 xl:w-60' />
        <KidsStar className='kid-twinkle absolute left-[4%] top-[9rem] size-6 sm:size-7' />
        <KidsStar tone='sky' className='kid-twinkle absolute right-[6%] top-[52%] size-5 [animation-delay:500ms] sm:size-6' />
        <KidsStar tone='berry' className='kid-twinkle absolute bottom-[42%] left-[2%] hidden size-5 [animation-delay:900ms] sm:block' />
        <KidsStar tone='grass' className='kid-twinkle absolute bottom-[26%] right-[3%] hidden size-6 [animation-delay:1300ms] md:block' />
    </div>
)
